import { useLingui } from "@lingui/react/macro";
import { Button, IconButton } from "@offstreetparking/design-system/forms";
import { ChevronLeftIcon } from "@offstreetparking/design-system/icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@offstreetparking/design-system/icons/ChevronRightIcon";
import { LocationIcon } from "@offstreetparking/design-system/icons/LocationIcon";
import { QrCodeIcon } from "@offstreetparking/design-system/icons/QrCodeIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RouterOutputs } from "../../../../api/types";
import { LazyQRCodeSVG } from "./LazyQRCodeSVG";
export const ParcsCodeQRCarousel = ({
	parcsCodes,
	initialQrIndex = 0,
}: {
	parcsCodes: RouterOutputs["permit"]["getPermitByPublicIdentifier"]["parcsCodes"];
	initialQrIndex?: number;
}) => {
	const { t } = useLingui();
	const [currentQrIndex, setCurrentQrIndex] = useState(initialQrIndex);
	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [touchEnd, setTouchEnd] = useState<number | null>(null);
	const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const minSwipeDistance = 50;
	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null);
		if (e.targetTouches[0]) {
			setTouchStart(e.targetTouches[0].clientX);
		}
	};
	const handleTouchMove = (e: React.TouchEvent) => {
		if (e.targetTouches[0]) {
			setTouchEnd(e.targetTouches[0].clientX);
		}
	};
	const handleTouchEnd = () => {
		if (!touchStart || !touchEnd) {
			return;
		}
		const distance = touchStart - touchEnd;
		const isLeftSwipe = distance > minSwipeDistance;
		const isRightSwipe = distance < -minSwipeDistance;
		if (isLeftSwipe && parcsCodes.length > 1) {
			setCurrentQrIndex((prev) => (prev < parcsCodes.length - 1 ? prev + 1 : 0));
		} else if (isRightSwipe && parcsCodes.length > 1) {
			setCurrentQrIndex((prev) => (prev > 0 ? prev - 1 : parcsCodes.length - 1));
		}
	};
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (parcsCodes.length <= 1) {
				return;
			}
			if (e.key === "ArrowLeft") {
				setCurrentQrIndex((prev) => (prev > 0 ? prev - 1 : parcsCodes.length - 1));
			} else if (e.key === "ArrowRight") {
				setCurrentQrIndex((prev) => (prev < parcsCodes.length - 1 ? prev + 1 : 0));
			}
		},
		[parcsCodes.length],
	);
	useMemo(() => {
		if (parcsCodes.length > 1) {
			window.addEventListener("keydown", handleKeyDown);
			return () => {
				window.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [handleKeyDown, parcsCodes.length]);
	useEffect(
		function recenterCarousel() {
			if (buttonRefs.current[currentQrIndex]) {
				buttonRefs.current[currentQrIndex]?.scrollIntoView({
					behavior: "smooth",
					block: "nearest",
					inline: "center",
				});
			}
		},
		[currentQrIndex],
	);
	const currentParcsCode = parcsCodes[currentQrIndex];
	if (!currentParcsCode) {
		return null;
	}
	return (
		<div
			className="flex flex-col items-center"
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			aria-label={t`Open QR Code`}
		>
			<div className="overflow-hidden rounded-md border-2 border-neutral-faded">
				<div className="bg-black p-3 text-center [print-color-adjust:exact]">
					<Text size="lg" weight="bold" className="text-white">
						{currentParcsCode.plate || ""}
					</Text>
					<div className="flex items-center justify-center gap-1">
						<LocationIcon size={12} className="text-white" />
						<Text size="xs" className="text-white">
							{currentParcsCode.locations.map(({ name }) => name).join(", ")}
						</Text>
					</div>
				</div>
				<div className="flex items-center justify-center bg-white p-6">
					<LazyQRCodeSVG size={180} value={currentParcsCode.code} level="H" />
				</div>
			</div>

			{parcsCodes.length > 1 && (
				<div className="mt-6 flex w-full max-w-lg items-center gap-4">
					<IconButton
						intent={"secondary"}
						size="xs"
						icon={ChevronLeftIcon}
						onClick={() => setCurrentQrIndex((prev) => (prev > 0 ? prev - 1 : parcsCodes.length - 1))}
						aria-label={t`Previous`}
						className="shrink-0"
					/>

					<div className="min-w-0 flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
						<div className="flex gap-2">
							{parcsCodes.map((parcsCode, index) => {
								const isCurrent = index === currentQrIndex;
								return (
									<Button
										key={parcsCode.code}
										type="button"
										onClick={() => setCurrentQrIndex(index)}
										intent="secondary"
										size="lg"
										leftIcon={QrCodeIcon}
										className={`w-32 shrink-0 text-left ${isCurrent ? "border-primary" : ""} [&>svg]:!h-6 [&>svg]:!w-6`}
										isActive={isCurrent}
										ref={(el) => {
											buttonRefs.current[index] = el;
										}}
									>
										<span className="flex flex-col items-start gap-0">
											<Text As="span" size="xs" weight="bold">
												{parcsCode.plate || ""}
											</Text>
											<Text size="xs" As="span" intent="muted" className="max-w-[60px] truncate">
												{parcsCode.locations.map(({ name }) => name).join(", ")}
											</Text>
										</span>
									</Button>
								);
							})}
						</div>
					</div>

					<IconButton
						icon={ChevronRightIcon}
						type="button"
						intent={"secondary"}
						size="xs"
						onClick={() => setCurrentQrIndex((prev) => (prev < parcsCodes.length - 1 ? prev + 1 : 0))}
						aria-label={t`Next`}
						className="shrink-0"
					/>
				</div>
			)}
		</div>
	);
};
